/**
 * A public page's code and its first read, started as the app loads instead of
 * once the page renders.
 *
 * A lazy route only asks for its chunk when it first renders. On a hard load
 * that is after the entry has evaluated, i18n has started, AuthProvider has
 * resolved getSession() and AppReadyProvider has re-rendered, and the page's
 * first read then waits for that chunk to download and evaluate. On a phone's
 * first visit (Slow 4G, 4x CPU) that was ~0.4 s of idle network before the
 * chunk, 0.6-1.2 s for it, then the read
 * (docs/plans/public-pages-performance-2026-10-16.md, 3.2 and Stage 4).
 *
 * So, on a hard load of a public page, App calls startPublicRoute() as it
 * evaluates, and:
 *
 *   - the page's import starts, with the same import its route renders, so the
 *     route finds the module already on its way (index.html has usually started
 *     the download already: scripts/vite/publicRoutePreload.mjs);
 *   - on the home page, the catalogue, a service's page and its form, the
 *     page's first read starts into the query cache the page reads, under the
 *     key and with the fetcher the page's own useQuery uses. The page finds it
 *     in flight or answered and does not read it again.
 *
 * Nothing about what renders, or what suspends where, changes: the routes
 * still render these pages through React.lazy. A failed early import or read is
 * forgotten; the route's own import and the page's own useQuery report failures
 * as they always have.
 *
 * The paths are in publicRoutes.json, which the build reads too, to write each
 * route's chunks into index.html. The first route that matches wins, so the
 * CAPE pages come before the service-slug routes they would otherwise match, as
 * React Router ranks a static segment above a parameter.
 */

import { queryClient } from "@/lib/query-client";
import { publicServicesQuery } from "@/hooks/queries/publicServicesQuery";
import { publicServiceDetailQuery } from "./publicServiceQueries";
import routes from "./publicRoutes.json";

/**
 * One import per page load, shared: the route's lazy component is handed the
 * promise the early start began. A failed import is forgotten, so a later
 * render tries again, as a lazy route's own import always has.
 */
function once<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => {
    pending ??= load().catch((error: unknown) => {
      pending = null;
      throw error;
    });
    return pending;
  };
}

/**
 * Each public page's import, shared by App's routes and the early start.
 * publicRoutes.json names each page's module, and publicRoutePreload.test.ts
 * holds the two together.
 */
export const publicPages = {
  HomePage: once(() => import("./HomePage")),
  ServiceCataloguePage: once(() => import("./ServiceCataloguePage")),
  ServiceDetailPage: once(() => import("./ServiceDetailPage")),
  ApplicationFormPage: once(() => import("./ApplicationFormPage")),
  TrackApplicationPage: once(() => import("./TrackApplicationPage")),
  ContinueApplicationPage: once(() => import("./ContinueApplicationPage")),
  // An unlisted service's link pages. Their code starts early like any other
  // page's; their read does not (no firstReads entry), because what it
  // answers depends on the visitor's session and account.
  UnlistedServicePage: once(() => import("./UnlistedServicePage")),
  UnlistedApplicationFormPage: once(
    () => import("./UnlistedApplicationFormPage"),
  ),
  HelpPage: once(() => import("./HelpPage")),
  GuidePage: once(() => import("./GuidePage")),
  AboutPage: once(() => import("./AboutPage")),
  PrivacyPage: once(() => import("./PrivacyPage")),
  TermsPage: once(() => import("./TermsPage")),
  CapeDirectoryPage: once(() => import("./CapeRegistration/CapeDirectoryPageUI")),
  CapeApplicationFormPage: once(
    () => import("./CapeRegistration/CapeApplicationForm/CapeApplicationFormUI"),
  ),
  AccessibilityPage: once(() => import("./AccessibilityPage")),
  ExternalReviewerPage: once(() => import("./ExternalReviewerPage")),
};

export type PublicPage = keyof typeof publicPages;

/**
 * The first read of each page that has one known from its URL. Each uses the
 * page's own query options, so the key and the fetcher cannot drift apart.
 * The form's options live in its own chunk, beside the form's code (they pull
 * in the form-schema helpers), so it is imported here rather than carried in
 * the entry; index.html has usually fetched it with the vendors already.
 */
const firstReads: Partial<Record<PublicPage, (params: string[]) => void>> = {
  HomePage: () => void queryClient.prefetchQuery(publicServicesQuery()),
  ServiceCataloguePage: () =>
    void queryClient.prefetchQuery(publicServicesQuery()),
  ServiceDetailPage: ([slug]) =>
    void queryClient.prefetchQuery(publicServiceDetailQuery(slug)),
  ApplicationFormPage: ([slug]) =>
    void import("./serviceFormQuery")
      .then(({ serviceFormQuery }) =>
        queryClient.prefetchQuery(serviceFormQuery({ slug })),
      )
      .catch(() => undefined),
};

const compiledRoutes = routes.map(({ page, path }) => ({
  page: page as PublicPage,
  pattern: new RegExp(path, "i"),
}));

/**
 * The path as React Router matches it (decodePath in @remix-run/router): each
 * segment decoded, a decoded "/" kept escaped, and the raw path if any segment
 * is malformed. A slug is then the same string useParams() gives the page.
 */
function decodePath(pathname: string): string {
  try {
    return pathname
      .split("/")
      .map((segment) => decodeURIComponent(segment).replace(/\//g, "%2F"))
      .join("/");
  } catch {
    return pathname;
  }
}

/** The public page a path renders, and its URL parameters; null for any other path. */
export function matchPublicRoute(
  pathname: string,
): { page: PublicPage; params: string[] } | null {
  const path = decodePath(pathname);
  for (const { page, pattern } of compiledRoutes) {
    const match = pattern.exec(path);
    if (match) return { page, params: match.slice(1) };
  }
  return null;
}

/**
 * Starts the page's import and its first read, for the page `pathname`
 * renders. Returns the page, or null where the path is not a public page (the
 * admin shell, sign-in, the auth callback, a 404), which starts nothing.
 */
export function startPublicRoute(pathname: string): PublicPage | null {
  const route = matchPublicRoute(pathname);
  if (!route) return null;
  firstReads[route.page]?.(route.params);
  // The catch only keeps an early failure from going unhandled: the route's
  // lazy component asks again when it renders, and reports it there.
  publicPages[route.page]().catch(() => undefined);
  return route.page;
}
